import React, { useCallback, useMemo } from 'react';
import { ISchema, PermissionKey } from '../../../utils/apollo/graphqlSchema';
import { permissionActionName, permissionDictionary } from '../utils';
import { IExpandableTableItem } from '../../../components/ExpandableTable/types';
import styled from 'styled-components';
import TickCheckbox from '../../../components/Checkbox/TickCheckbox';
import { ICustomPermissionAssignmentInput } from '../../AdminSettings/Roles/RoleModify';
import PermissionSchemasSelector from '../PermissionSchemasSelector';
import { Updater } from 'use-immer';
import useFeatureFilteredPermissionList from './useFeatureFilteredPermissionList';
import { IDisplayedPermissionItem, PermissionListType } from '../types';
import { theme } from '../../../styles/theme';

interface IProps {
  permissionInputs: ICustomPermissionAssignmentInput[];
  setPermissionInputs: Updater<ICustomPermissionAssignmentInput[]>;
}

const PermissionContainer = styled.div`
  padding: 12px 16px;
`;
const ActionContainer = styled.div`
  :not(:last-child) {
    margin-bottom: 12px;
  }
`;
const ActionName = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${theme.colors.secondary900};
  margin-bottom: 8px;
`;
const PermissionItemContainer = styled.div`
  display: flex;
  flex-direction: column;
  border: solid 1px ${theme.colors.border};
`;
const PermissionItem = styled.div<{ $nonWhiteBackground: boolean }>`
  font-size: 12px;
  padding: 4px 8px;
  display: flex;
  align-items: center;
  column-gap: 4px;
  background-color: ${({ $nonWhiteBackground }) =>
    $nonWhiteBackground ? theme.colors.level2 : theme.colors.level1};
`;

const PermissionColumn = styled.span`
  width: 240px;
`;

const CheckBoxContainer = styled.span`
  width: 86px;
`;

/**
 * this hook is going to use PermissionAssignments to generate a list of expandable table
 * The expandable table will have the following structure in displayedPermissionList.
 * Each permission item will have a checkbox to toggle the isActive state and also a schema dropdown
 * to select the schemas if there is more than one assignable schemas in permission.
 *
 * @param permissionInputs is the current permissionInputs that is being edited
 * @param setPermissionInputs is the setter for permissionInputs
 */
const usePermissionEditableList = ({
  setPermissionInputs,
  permissionInputs,
}: IProps) => {
  // click checkbox to toggle isActive state on selected permission
  const onClickCheckbox = useCallback(
    (permissionKey: PermissionKey) => {
      const permissionInput = permissionInputs.find((permissionInput) => {
        return permissionInput.permissionKey === permissionKey;
      });
      if (permissionInput) {
        setPermissionInputs((draft) => {
          const index = draft.findIndex((permissionInput) => {
            return permissionInput.permissionKey === permissionKey;
          });
          if (index !== -1) {
            draft[index] = {
              ...permissionInput,
              isActive: !permissionInput.isActive,
            };
          }
        });
      }
    },
    [permissionInputs, setPermissionInputs],
  );

  const onSelectPermissionSchema = useCallback(
    (permissionKey: PermissionKey, schemas: Pick<ISchema, 'name' | 'id'>[]) => {
      const permissionInput = permissionInputs.find((permissionInput) => {
        return permissionInput.permissionKey === permissionKey;
      });
      if (permissionInput) {
        setPermissionInputs((draft) => {
          const index = draft.findIndex((permissionInput) => {
            return permissionInput.permissionKey === permissionKey;
          });
          if (index !== -1) {
            draft[index] = { ...permissionInput, schemas };
          }
        });
      }
      return;
    },
    [permissionInputs, setPermissionInputs],
  );

  const featureFilteredPermissionList = useFeatureFilteredPermissionList();
  const getEditablePermissionItems = useCallback(
    (displayedPermission: IDisplayedPermissionItem) => {
      return (
        <PermissionContainer key={displayedPermission.title}>
          {displayedPermission.items.map((permissionProps, index) => {
            return (
              <ActionContainer key={'permission-props-' + index}>
                <ActionName>
                  {permissionActionName[permissionProps.actionKey]}
                </ActionName>
                <PermissionItemContainer>
                  {permissionProps.permissionKeys.map(
                    (permissionKey, index) => {
                      const permissionName =
                        permissionDictionary[permissionKey];
                      const currPermission = permissionInputs.find(
                        (permission) => {
                          return permission.permissionKey === permissionKey;
                        },
                      );
                      if (!currPermission) {
                        return null;
                      }
                      return (
                        <PermissionItem
                          $nonWhiteBackground={index % 2 === 0}
                          key={'permission-item-' + permissionName + index}
                        >
                          <PermissionColumn>{permissionName}</PermissionColumn>
                          <CheckBoxContainer>
                            <TickCheckbox
                              checked={currPermission.isActive}
                              onClick={() => onClickCheckbox(permissionKey)}
                            />
                          </CheckBoxContainer>
                          {currPermission.availableSchemas.length > 1 ? (
                            <PermissionSchemasSelector
                              setSchemas={(schemas) =>
                                onSelectPermissionSchema(permissionKey, schemas)
                              }
                              selectedSchemas={currPermission.schemas}
                              isMulti={true}
                              schemaOptions={currPermission.availableSchemas}
                            />
                          ) : (
                            currPermission.schemas?.[0]?.name
                          )}
                        </PermissionItem>
                      );
                    },
                  )}
                </PermissionItemContainer>
              </ActionContainer>
            );
          })}
        </PermissionContainer>
      );
    },
    [onClickCheckbox, permissionInputs, onSelectPermissionSchema],
  );
  // return the element contains the list of editable permission items. and fit it in ExpandableTable component
  // if the permission is a category, it will have a list of items inside it.
  const editablePermissionTableList: IExpandableTableItem[] = useMemo(() => {
    return featureFilteredPermissionList.flatMap((displayedPermission) => {
      if (displayedPermission.type === PermissionListType.Items) {
        return {
          title: displayedPermission.title,
          secondColumnTitle: 'Schemas',
          type: PermissionListType.Items,
          childElement: getEditablePermissionItems(displayedPermission),
        };
      } else {
        return {
          title: displayedPermission.title,
          type: PermissionListType.Category,
          secondColumnTitle: 'Schemas',
          childElements: displayedPermission.list.map(
            (displayedPermissionItem) => {
              return {
                title: displayedPermissionItem.title,
                type: PermissionListType.Items,
                childElement: getEditablePermissionItems(
                  displayedPermissionItem,
                ),
              };
            },
          ),
        };
      }
    });
  }, [getEditablePermissionItems, featureFilteredPermissionList]);
  return {
    editablePermissionTableList,
  };
};

export default usePermissionEditableList;
